import { types, flow, getParent } from 'mobx-state-tree';
import { pickBy, forEach, snakeCase } from 'lodash';
import qs from 'qs';
import { getOr } from 'lodash/fp';
// import { when } from 'mobx';
import {
  convertDestinationMarketsEnumsToStrings,
  convertSourceMarketsEnumsToStrings,
  createStrategyProperties,
  convertConditionToNumber,
  convertStrategyTypeToString,
  checkForVeroFailedWordsToUpdate,
} from '../../utils/Discover';
import Filters from './Filters';
import { Discovery, Item } from '../../models/DiscoverNew';
import Strategies from './Strategies';
import QList from './QList';

import WSMessage from '../../websockets/wsMessage';
import websockets from '../../websockets';
import Account from '../../models/Account';
import ENUMS from '../../constants/enums';

const DiscoveryStore = types
  .model('DiscoveryStore', {
    discoveries: types.optional(types.map(Discovery), {}),
    discoverMarket: types.optional(types.number, 0),
    listingMarket: types.optional(types.number, 0),
    discoverMethod: types.optional(types.number, 0),
    sellerAccountCode: types.optional(types.string, ''),
    isWithVariations: false,
    automaticallyListItems: false,
    skipVeroValidation: false,
    useCollageImage: false,
    rotateImage: false,
    skipOptimization: false,
    discoverValue: types.optional(types.array(types.frozen), []),
    discoverValueString: '',
    discoverStrategy: types.optional(types.union(types.number, types.string), 0),
    discoveryId: types.maybe(types.number),
    isAsyncComeBack: true,
    qList: types.optional(QList, {}),
    isFirstEntrance: types.maybe(types.boolean),
    strategies: types.optional(types.map(Strategies), {}),
    sellerAccountPolicies: types.optional(types.array(types.frozen), []),
    tableSortType: 1,
    tableSortIsAscending: false,
    allAvailableSourceMarketPlaces: types.optional(types.array(types.number), [4, 3, 2, 1]),
    sortIsAscending: false,
    qListDataLoaded: false,
    sortType: 1,
    filters: types.optional(Filters, {}),
    sellersAccounts: types.optional(types.array(Account), []),
    rotateImageOptimization: false,
    collageImageOptimization: false,
  })
  .volatile(() => ({
    urlRoot: '/api',
  }))
  .views(self => ({
    get rangedProfitSelected() {
      return self.discoverStrategy === 'ranged';
    },
    get penetrationProfitSelected() {
      return self.discoverStrategy === 3;
    },
    get transport() {
      return getParent(self).transport5008;
    },
    get transport5007() {
      return getParent(self).transport5007V2;
    },
    get enums() {
      return getParent(self).enumsStore.getEnumsList;
    },
    get enumsReverse() {
      return getParent(self).enumsStore.getEnumsReverseList;
    },
    get showToast() {
      return getParent(self).toastsStore.showToast;
    },
    get browserHistory() {
      return getParent(self).routingStore.browserHistory;
    },
    get getDiscoveryIds() {
      return self.discoveryId ? [self.discoveryId] : [];
    },
    get accountCode() {
      return getParent(self).sessionStore.accountCode;
    },
    get sellerAccountName() {
      return (self.sellersAccounts.find(acc => acc.id === self.sellerAccountCode) || { name: '' })
        .name;
    },
    get isSingleDiscover() {
      return Object.keys(self.discoverValue).length === 1;
    },
    get defaultQuantityToList() {
      return getParent(self).settingsStore.listing.listingOptions.defaultQuantityToList || 1;
    },
    get basicUserInfoValid() {
      return getParent(self).settingsStore.userProfile.basicInfoValid;
    },
    get initialized() {
      return getParent(self).settingsStore.initialized;
    },
    get isBetaUser() {
      return getParent(self).settingsStore.userProfile.isBetaUser;
    },
    get availableSourceMarketPlaces() {
      // dict to map sources to their corresponding number
      const sourceMarketplace = {
        AZ_US: 1,
        WM: 2,
        ALI_CN: 3,
        ALI_US: 4,
        AZ_UK: 7,
        AZ_CA: 8,
        CJ_US: 9,
        CJ_CN: 10,
        AZ_DE: 12,
        AZ_IT: 13,
        AZ_FR: 14,
        AZ_ES: 15,
      };
      return (
        getParent(self)
          .settingsStore.markets.sources.filter(item => item.id !== 'CATALOG')
          .map(item => sourceMarketplace[item.id]) || [1]
      );
    },
    get availableDestinationMarketPlaces() {
      // dict to map sources to their corresponding number
      const sourceMarketplace = {
        EBAY_US: 4,
        AZD_US: 5,
        EBAY_GB: 6,
        EBAY_CA: 7,
        EBAY_DE: 8,
        EBAY_IT: 9,
        EBAY_FR: 10
      };
      return (
        getParent(self)
          .settingsStore.markets.destinations
          .map(item => sourceMarketplace[item.id]) || [1]
      );
    },
  }))
  .actions(self => ({
    getSellerAccountName(sellerAccountCode) {
      return (self.sellersAccounts.find(acc => acc.id === sellerAccountCode) || { name: '' }).name;
    },

    setDiscoveryId: id => {
      self.discoveryId = id;
    },

    setIsWithVariations: bool => {
      self.isWithVariations = bool;
    },

    setAutomaticallyListItems: bool => {
      self.automaticallyListItems = bool;
    },

    setSkipVeroValidation: bool => {
      self.skipVeroValidation = bool;
    },

    setUseCollageImage: bool => {
      self.useCollageImage = bool;
    },

    setRotateImage: bool => {
      self.rotateImage = bool;
    },

    setSkipOptimization: bool => {
      self.skipOptimization = bool;
    },

    setQList: data => {
      self.qList = data;
    },
    setIsFirstEntrance: bool => {
      self.isFirstEntrance = bool;
    },

    setIsAsyncComeBack: bool => {
      self.isAsyncComeBack = bool;
    },

    setSortIsAscending: bool => {
      self.sortIsAscending = bool;
    },

    setSortType: type => {
      self.sortType = type;
    },

    setStrategyProperties: (strategyType, property, value) => {
      const newStrategies = { ...self.strategies };
      newStrategies.get(strategyType).properties[property] = value;
      self.strategies = newStrategies;
    },

    setCollageOptimization: bool => {
      self.collageImageOptimization = bool;
    },

    setRotateOptimization: bool => {
      self.rotateImageOptimization = bool;
    },

    getQList: flow(function* getQList() {
      const currencyId = getParent(self).settingsStore.userProfile.defaultCurrencyId;
      const marketplace = getParent(self).settingsStore.userProfile.defaultSourceMarketplace;
      try {
        self.qListDataLoaded = false;
        const { data } = yield self.transport.fetch(
          `${self.urlRoot}/discovery-new/v1/discovery/queue-page?currencyId=${currencyId}&sourceMarketplace=${marketplace}`,
          {
            newerThanDays: 2,
          },
          {},
        );
        self.setQList(data.context);
        self.setIsFirstEntrance(data.isFirstEntrance);
      } catch (error) {
        console.error(error);
      } finally {
        self.qListDataLoaded = true;
      }
    }),
    clearAll: flow(function* clearAll() {
      try {
        yield self.transport5007.delete('/discovery-new/v1/discovery/all/2');
        self.getQList();
      } catch (error) {
        console.error(error);
      }
    }),
    getImportReport: flow(function* getImportReport(id) {
      try {
        const { data } = yield self.transport.fetch(
          `${self.urlRoot}/discovery-new/v1/listing/listing-export?discoveryId=${id}`,
        );
        return data;
      } catch (error) {
        console.error(error);
        return null;
      }
    }),

    deleteDiscovery: flow(function* deleteDiscovery(id) {
      try {
        const { data } = yield self.transport.delete(
          `${self.urlRoot}/discovery-new/v1/discovery/${id}`,
        );
        if (data.isSuccess) {
          self.getQList();
        }
        return data.isSuccess;
      } catch (error) {
        return error;
      }
    }),
    uploadImages: flow(function* uploadImages(formData) {
      try {
        const { data } = yield self.transport.post(
          `${self.urlRoot}/discovery-new/v1/item/picture`,
          formData,
        );
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'Upload successful!',
        });
        return data;
      } catch (error) {
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'Upload failed',
        });
        return Promise.reject(error);
      }
    }),
    deleteImage: flow(function* deleteImage(id) {
      try {
        const { data } = yield self.transport.post(
          `${self.urlRoot}/discovery-new/v1/item/remove-picture`,
          { id },
        );
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'Delete successful!',
        });
        return data;
      } catch (error) {
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'Delete failed',
        });
        return Promise.reject(error);
      }
    }),
    addItemSpecific: flow(function* addItemSpecific(params) {
      try {
        const { data } = yield self.transport.post(
          `${self.urlRoot}/discovery-new/v1/item/item-specific`,
          params,
        );
        console.log(data);
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'Specific Added successfully!',
        });
        return data;
      } catch (error) {
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'Adding Specific failed',
        });
        return null;
      }
    }),
    deleteItemSpecific: flow(function* deleteItemSpecific(discoveryItemSpecificId) {
      try {
        const { data } = yield self.transport.post(
          `${self.urlRoot}/discovery-new/v1/item/remove-item-specific`,
          { discoveryItemSpecificId },
        );
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'Delete successful!',
        });
        return data;
      } catch (error) {
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'Delete failed',
        });
        return Promise.reject(error);
      }
    }),
    getDefaultPolicyCode: () =>
      self.sellerAccountPolicies.length &&
      self.sellerAccountPolicies.find(policy => policy.isDefault),

    sendDiscover: flow(function* sendDiscover() {
      const reqData = {
        accountCode: self.accountCode,
        sellerAccountCode: self.sellerAccountCode,
        sourceId: self.discoverMarket,
        destinationId: self.listingMarket,
        discoveryBody: self.discoverValue.toJSON(),
        discoveryStrategy: self.rangedProfitSelected
          ? self.strategies.toJSON()[self.enums.STRATEGY.FIXED_PROFIT].properties
          : self.strategies.toJSON()[self.discoverStrategy].properties,
        usePredefined: self.rangedProfitSelected,
        discoveryMethod: 2,
        policyCode:
          self.listingMarket === self.enums.DESTINATION_MARKETPLACE.EBAY_US ||
          self.listingMarket === self.enums.DESTINATION_MARKETPLACE.EBAY_GB ||
          self.listingMarket === self.enums.DESTINATION_MARKETPLACE.EBAY_CA ||
          self.listingMarket === self.enums.DESTINATION_MARKETPLACE.EBAY_DE ||
          self.listingMarket === self.enums.DESTINATION_MARKETPLACE.EBAY_IT ||
          self.listingMarket === self.enums.DESTINATION_MARKETPLACE.EBAY_FR ||
          self.listingMarket === self.enums.DESTINATION_MARKETPLACE.EBAY_ES
            ? self.getDefaultPolicyCode()?.code
            : null,
        isWithVariations: self.isWithVariations,
        automaticallyListItems: self.automaticallyListItems,
        skipVeroValidation: self.skipVeroValidation,
        useCollageImage: self.useCollageImage,
        rotateImage: self.rotateImage,
        defaultQuantity: self.defaultQuantityToList,
        skipOptimization: self.skipOptimization,
      };
      console.log(reqData);
      try {
        if (!self.basicUserInfoValid) {
          getParent(self).settingsStore.toggleSettings(ENUMS.SETTINGS_TABS.USER_PROFILE, true);
          const respError = {
            response: {
              data: {
                errorMessage: `Please update your User Profile - Name, Phone Number and Country are required`,
              },
            },
          };
          throw Object.assign(new Error('Basic data not valid'), respError);
        } else if (self.discoverValue.length >= 5000) {
          const respError = {
            response: {
              data: {
                errorMessage: `Please use less than 5000 items in one discovery. You used ${
                  self.discoverValue.length
                }.`,
              },
            },
          };
          throw Object.assign(new Error('Too many ids'), respError);
        }
        self.setIsAsyncComeBack(false);
        const { data } = yield self.transport.post(
          `${self.urlRoot}/discovery-new/v1/pre-discovery`,
          reqData,
          null,
          {},
        );
        console.log(data);
        self.setDiscoveryId(data);
        self.setIsAsyncComeBack(true);
        const { onboardingStore } = getParent(self);
        if (onboardingStore.flow !== 'done') {
          onboardingStore.disableTutorial();
          onboardingStore.finishWizard();
        }
        return data;
      } catch (error) {
        const { data } = error.response;
        return data;
      }
    }),

    createItems: (data, discoveryId, offset = null) => {
      if (
        offset !== null &&
        self.discoveries.get(discoveryId).discoveryItems.length &&
        offset !== self.discoveries.get(discoveryId).discoveryItems.length
      ) {
        return;
      }
      data.discoveryItems.forEach(item => {
        // self.discoveries.get(discoveryId).rowsSelected.push(item.id);
        // if (item.variations.length) {
        //   self.discoveries.get(discoveryId).variationsSelected.set(item.id, []);
        //   item.variations.forEach(variation => {
        //     self.discoveries
        //       .get(discoveryId)
        //       .variationsSelected.get(item.id)
        //       .push(variation.id);
        //   });
        // }

        self.discoveries.get(discoveryId).discoveryItems.push(Item.create(item));
      });
    },

    getFiltersQuery: () => {
      let queryString = '';
      forEach(self.filters, (val, key) => {
        if (val) {
          queryString += `&${key}=${val}`;
        }
      });
      if (queryString.split('&').length <= 3) {
        queryString = '';
      }
      return queryString;
    },

    // eslint-disable-next-line consistent-return
    fetchByDiscoveryId: flow(function* fetchByDiscoveryId(id, isDiscoveredByFilter = null) {
      try {
        let currentDiscovery = self.discoveries.get(id);
        const returnedItemsCount =
          currentDiscovery && currentDiscovery.discoveryItems
            ? currentDiscovery.discoveryItems.length
            : 0;
        const reqBody = {
          sortBy: self.sortType,
          offset: 0,
          pageSize: Math.max(returnedItemsCount, 20),
          isAscending: self.sortIsAscending,
          ...self.filters.getAllFiltersAsObject,
        };
        const { data } = yield self.transport.post(
          `${self.urlRoot}/discovery-new/v1/discovery/${id}/query`,
          isDiscoveredByFilter ? reqBody : {},
          null,
          {},
        );
        if (data && data.statusId === self.enums.DISCOVERY_STATUS.FAILED) {
          if (
            data.discoveryItems.length === 1 &&
            data.discoveryItems[0].statusId === self.enums.DISCOVERY_ITEM_STATUS.VALIDATION_FAILED
          ) {
            self.showToast({
              type: self.enums.TOAST_TYPES.WARNING,
              message: 'The item failed on Vero / Blacklist validation',
            });
            // an item failed validation, returning the item status to the component
            console.log('got-tovero-failure');
            return {
              statusId: self.enums.DISCOVERY_ITEM_STATUS.VALIDATION_FAILED,
              id: data.id,
              failedTitleWords: data?.discoveryItems[0]?.titleFailedWords ?? [],
              failedBrandWords: data?.discoveryItems[0]?.brandFailedWords ?? [],
              failedDescriptionWords: data?.discoveryItems[0]?.descriptionFailedWords ?? [],
              failedSellerWords: data?.discoveryItems[0]?.sellerFailedWords ?? [],
            };
          } else if (getOr(0, 'statistics.failedVeroCount', data) !== 0) {
            console.log('got-to-vero-fail-count');
            return {
              statusId: self.enums.DISCOVERY_ITEM_STATUS.VALIDATION_FAILED,
              id: data.id,
              failedTitleWords: data?.discoveryItems[0]?.titleFailedWords ?? [],
              failedBrandWords: data?.discoveryItems[0]?.brandFailedWords ?? [],
              failedDescriptionWords: data?.discoveryItems[0]?.descriptionFailedWords ?? [],
            };
          }
          self.showToast({
            type: self.enums.TOAST_TYPES.WARNING,
            message: "Item can't be discovered",
          });
          return { statusId: data.statusId };
        }

        if (data && data.statusId === self.enums.DISCOVERY_STATUS.COMPLETED) {
          console.log('completed');
          if (!data.discoveryItems.length) {
            return null;
          }
          self.discoverMarket = self.enums.NEW_DISCOVERY_SOURCE_NAMES[data.sourceMarketplaceId];
          self.listingMarket =
            self.enums.DESTINATION_MARKETPLACE[
              data?.pricingStrategy?.destinationMarketplaceId ?? 0
            ];
          self.discoverStrategy = data?.pricingStrategy?.breakEven
            ? data?.pricingStrategy?.strategyType ?? ''
            : 'ranged';
          if (self.discoveries.get(data.id)) {
            self.discoveries.delete(data.id);
          }
          console.log('deleted from discoveries');
          self.discoveries.set(data.id, Discovery.create(data));
          console.log('set + create successful');
          currentDiscovery = self.discoveries.get(data.id);
          console.log('get successful');
          currentDiscovery.initDiscovery();
          console.log('init successful');
          if (
            !self.qList.completedDiscoveries.length
            // !self.qList.discoveriesInProgress.length ||
            // !self.qList.failedDiscoveries.length ||
            // !self.qList.listedDiscoveries.length
          ) {
            yield self.getQList();
          }
          self.discoverMethod = data.methodType;
          self.discoverStrategy = data?.pricingStrategy?.breakEven
            ? data?.pricingStrategy?.strategyType
            : 'ranged';
          self.sellerAccountCode = getOr('', 'pricingStrategy.sellerAccountId', data);
          console.log('before create items');
          self.createItems(data, data.id);
          console.log('got to return data');
          return data;
        }
      } catch (error) {
        console.error(error);
        return error;
      }
    }),

    fetchPaginationItems: flow(function* fetchPaginationItems(
      discoveryId,
      isDiscoveredByFilter = null,
      all = false,
    ) {
      const currentDiscovery = self.discoveries.get(discoveryId);
      try {
        const reqBody = {
          sortBy: self.sortType,
          offset: currentDiscovery.discoveryItems.length,
          pageSize: all
            ? currentDiscovery.statistics.discoveredItemsCount -
              currentDiscovery.discoveryItems.length
            : 20,
          isAscending: self.sortIsAscending,
          ...(isDiscoveredByFilter ? self.filters.getAllFiltersAsObject : {}),
        };
        const { data } = yield self.transport.post(
          `${self.urlRoot}/discovery-new/v1/discovery/${discoveryId}/query`,
          reqBody,
          null,
          {},
        );
        if (data) {
          self.createItems(data, data.id, currentDiscovery.discoveryItems.length);
          // currentDiscovery.statistics = data.statistics;
          const qCard = self.qList.completedDiscoveries.find(disc => disc.id === data.id);
          currentDiscovery.statistics = qCard ? qCard.statistics : null; // todo statistics remove when in response
        }
        if (!data.discoveryItems.length) {
          return null;
        }
        return data;
      } catch (error) {
        return error;
      }
    }),

    setSingleItemStrategies: flow(function* setSingleItemStrategies(
      itemId,
      propsToUpdate,
      discoveryId,
    ) {
      try {
        const reqBody = {
          propsToUpdate,
        };
        const { data } = yield self.transport.put(
          `${self.urlRoot}/discovery-new/v1/item/${itemId}/strategy/CalculateProfits`,
          reqBody,
          null,
          {},
        );
        if (data.isSuccess) {
          /* eslint-disable radix */
          const discovery = self.discoveries.get(discoveryId);
          discovery.discoveryItems.forEach(item => {
            if (item.id === itemId) {
              forEach(data.pricingStrategy, (val, key) => {
                switch (key) {
                  case 'profitPercent':
                  case 'profitValue':
                  case 'breakEven':
                  case 'salePrice': {
                    item.pricingStrategy[key] = parseFloat(val);
                    break;
                  }
                  default: {
                    item.pricingStrategy[key] = val;
                  }
                }
              });
            }
          });
          /* eslint-enable radix */
          return data;
        }
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: data.errorMessage,
          delay: 10000,
        });
        return null;
      } catch (error) {
        return error;
      }
    }),

    tryAgain: flow(function* tryAgain(id) {
      try {
        const { data } = yield self.transport.put(
          `${self.urlRoot}/discovery-new/v1/discovery/${id}/try-again`,
          {},
          null,
          {},
        );
        if (data.isSuccess) {
          self.getQList();
        }
        return data.isSuccess;
      } catch (error) {
        return error;
      }
    }),

    tryAgainDespiteVero: flow(function* tryAgainDespiteVero(id) {
      try {
        const { data } = yield self.transport.post(
          `${self.urlRoot}/discovery-new/v1/vero-validation/${id}/skip-vero-on-failed`,
          {},
          null,
          {},
        );
        console.log('data', data);
        if (data.isSuccessful) {
          self.getQList();
        }
        return data.isSuccessful;
      } catch (error) {
        return error;
      }
    }),

    stopProgress: flow(function* stopProgress(id) {
      try {
        const { data } = yield self.transport.put(
          `${self.urlRoot}/discovery-new/v1/discovery/${id}/stop`,
        );
        if (data.isSuccess) {
          self.getQList();
        }
        return data.isSuccess;
      } catch (error) {
        return error;
      }
    }),

    updateDiscoveryItemsInternal: (discoveryId, itemsToBeUpdated, data) => {
      itemsToBeUpdated.forEach(toBeUpdated => {
        if (!toBeUpdated.isVariant) {
          self.discoveries.get(discoveryId).discoveryItems.forEach(item => {
            if (item.id === toBeUpdated.id) {
              Object.keys(data).forEach(key => {
                if (key === 'quantity') {
                  item.pricingStrategy.quantity = parseFloat(data.quantity, 10);
                } else {
                  item[key] = data[key];
                }
              });
            }
          });
        } else if (toBeUpdated.mainItemId !== false) {
          self.discoveries.get(discoveryId).discoveryItems.forEach(item => {
            if (item.id === toBeUpdated.mainItemId) {
              item.variations.forEach(variation => {
                if (variation.id === toBeUpdated.id) {
                  Object.keys(data).forEach(key => {
                    if (key === 'quantity') {
                      variation.pricingStrategy.quantity = parseFloat(data.quantity, 10);
                    } else {
                      variation[key] = data[key];
                    }
                  });
                }
              });
            }
          });
        }
      });
    },

    updateDiscoveryItems: flow(function* updateDiscoveryItems(discoveryId, id, propsToUpdate) {
      const reqData = {
        propsToUpdate,
      };
      try {
        const currentDiscoveryId = discoveryId;
        const { data, config } = yield self.transport.put(
          `${self.urlRoot}/discovery-new/v1/item/${id}/edit`,
          reqData,
          null,
          {},
        );
        if (data.isSuccess) {
          const parsedPropsToUpdate = convertConditionToNumber(JSON.parse(config.data));
          const veroFailedWordsToUpdate = checkForVeroFailedWordsToUpdate(data);
          const parsedPropsToUpdateMerged = { ...parsedPropsToUpdate, ...veroFailedWordsToUpdate };
          self.updateDiscoveryItemsInternal(
            currentDiscoveryId,
            [{ id, isVariant: false }],
            parsedPropsToUpdateMerged,
          );
          self.showToast({
            type: self.enums.TOAST_TYPES.INFO,
            message: 'Item updated successfully',
          });
          return parsedPropsToUpdate;
        }
        return null;
      } catch (error) {
        return error;
      }
    }),

    addLabelsInternal: (discoveryId, labelsArray) => {
      const discovery = self.discoveries.get(discoveryId);
      discovery.discoveryItems.forEach(item => {
        labelsArray.forEach(label => {
          if (label.discoveryItemId === item.id) {
            item.itemLabels.push(label);
          }
        });
      });
    },

    updateDiscoveryItemsMulti: flow(function* updateDiscoveryItemsMulti(
      discoveryId,
      propsToUpdate,
    ) {
      const currentDiscovery = self.discoveries.get(discoveryId);
      const IdsToChange = currentDiscovery.rowsSelected.map(itemId => ({
        id: itemId,
        isVariant: false,
      }));
      currentDiscovery.rowsSelected.forEach(item => {
        const currentVariationsSelected = currentDiscovery.variationsSelected.get(item);
        if (currentVariationsSelected && currentVariationsSelected.length) {
          IdsToChange.push(
            ...currentVariationsSelected.map(variationId => ({
              id: variationId,
              isVariant: true,
              mainItemId: item,
            })),
          );
        }
      });
      const otherProps = pickBy(propsToUpdate, (val, key) => key !== 'labels');
      const labelProps = pickBy(propsToUpdate, (val, key) => key === 'labels');
      const successStatus = {
        labels: false,
        other: false,
      };

      try {
        const currentDiscoveryId = discoveryId;
        if (Object.keys(otherProps).length) {
          const { data, config } = yield self.transport.put(
            `${self.urlRoot}/discovery-new/v1/discovery/edit-multiple`,
            {
              discoveryId,
              idsToUpdate: IdsToChange,
              propsToUpdate: otherProps,
            },
            null,
            {},
          );
          if (data.isSuccess) successStatus.other = true;
          const parsedConfig = JSON.parse(config.data);
          self.updateDiscoveryItemsInternal(
            currentDiscoveryId,
            IdsToChange,
            parsedConfig.propsToUpdate,
          );
        } else {
          successStatus.other = true;
        }
        const itemsToAddLabel = currentDiscovery.discoveryItems.filter(
          x => x.itemLabels.some(y => y.name === labelProps.labels) === false,
        );
        if (Object.keys(labelProps).length && itemsToAddLabel.length > 0) {
          const labelsResponse = yield self.transport.post(
            `${self.urlRoot}/discovery-new/v1/item/bulk-label`,
            {
              discoveryItemIds: itemsToAddLabel.map(a => a.id),
              name: labelProps.labels,
            },
            null,
            {},
          );
          if (labelsResponse.data.length) {
            successStatus.labels = true;
            self.addLabelsInternal(currentDiscoveryId, labelsResponse.data);
          }
        } else
          self.showToast({
            type: self.enums.TOAST_TYPES.WARNING,
            message: 'Could not add the label. Already exists.',
          });

        return successStatus;
      } catch (error) {
        console.log(error);
        return error;
      }
    }),

    listSingleDiscovery: flow(function* listSingleDiscovery(reqData) {
      try {
        const { data } = yield self.transport.post(
          `${self.urlRoot}/discovery-new/v1/listing/single`,
          reqData,
          null,
        );
        console.log(data);

        self.discoveries.get(reqData.discoveryId).listStatus = data.status;
        if (!data.errors) {
          return true;
        }
        self.discoveries.get(reqData.discoveryId).listServerErrors = data.errors;
        // self.discoveries.get(reqData.discoveryId).errorMessage = data.errorMessage || getOr(null, 'responseObject.errorMessage', data);
        return false;
      } catch (error) {
        console.error(error);
        return false;
      }
    }),

    listBulkDiscovery: flow(function* listBulkDiscovery(discoveryId) {
      const discoveryItemIds = self.discoveries.get(discoveryId).rowsSelected;
      const reqBody = {
        includeVariations: self.discoveries.get(discoveryId).isMainSelected,
        discoveryId,
        discoveryItemIds,
      };
      try {
        const { data } = yield self.transport.post(
          `${self.urlRoot}/discovery-new/v1/listing/bulk`,
          reqBody,
          null,
        );
        self.discoveries.get(reqBody.discoveryId).listStatus = data.status;
        if (data.isSuccess || !data.errors) {
          return true;
        }
        self.discoveries.get(reqBody.discoveryId).listServerErrors = data.errors;
        return false;
      } catch (error) {
        console.log(error);
        return null;
      }
    }),

    listBigBulkDiscovery: flow(function* listBigBulkDiscovery(discoveryId, includeVariation) {
      const reqBody = {
        includeVariations: includeVariation,
        discoveryId,
      };
      try {
        const { data } = yield self.transport.post(
          `${self.urlRoot}/discovery-new/v1/listing/big-bulk`,
          reqBody,
          null,
        );
        self.discoveries.get(reqBody.discoveryId).listStatus = data.status;
        if (data.isSuccess || !data.errors) {
          return true;
        }
        self.discoveries.get(reqBody.discoveryId).listServerErrors = data.errors;
        return false;
      } catch (error) {
        console.log(error);
        return null;
      }
    }),

    instantList: flow(function* instantList(discoveryId) {
      try {
        const isOk = yield self.listBigBulkDiscovery(discoveryId, true);
        if (!isOk) {
          return isOk;
        }
        yield self.getQList();
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    }),

    getStrategyDefaults: flow(function* getStrategyDefaults(discMarket) {
      try {
        const { data } = yield self.transport5007.fetch(
          `strategy/v1/strategies?sourceMarketplaceId=${convertSourceMarketsEnumsToStrings(
            discMarket ?? self.discoverMarket,
          )}&destinationMarketplace=${convertDestinationMarketsEnumsToStrings(self.listingMarket)}`,
          null,
          {
            'Seller-Account-Id': self.sellerAccountCode,
          },
        );
        forEach(data, (val, key) => {
          const currentStrategyType = `${self.enums.STRATEGY[snakeCase(key).toUpperCase()]}`;
          self.strategies.set(currentStrategyType, Strategies.create({}));
          const currentStrategy = self.strategies.get(currentStrategyType);
          currentStrategy.properties = createStrategyProperties(
            val,
            currentStrategyType,
            self.enums.TYPE_OF_DISCOVERY_PROFIT_TYPE,
          );
        });
        return self.strategies;
      } catch (error) {
        console.error(error);
        return null;
      }
    }),

    setDefaultStrategyValues: flow(function* setDefaultStrategyValues(reqData) {
      const reqBody = {
        ...reqData.reqBody,
        sourceMarketplaceId: convertSourceMarketsEnumsToStrings(self.discoverMarket),
      };
      const strategyTypeString = convertStrategyTypeToString(Number(reqData.strategyType));
      try {
        const { data } = yield self.transport5007.put(
          `strategy/v1/Strategies/${strategyTypeString}`,
          reqBody,
          null,
          {
            'Seller-Account-Id': self.sellerAccountCode,
          },
        );
        console.log(data);
        return true;
      } catch (error) {
        return null;
      }
    }),

    getPoliciesForSellerAccount: flow(function* getPoliciesForSellerAccount(
      sellerAccountCode,
      discoverMarket,
    ) {
      const currentSellerAccountCode = sellerAccountCode || self.sellerAccountCode;
      const currentDiscoverMarket = discoverMarket || self.discoverMarket;
      try {
        const { data } = yield self.transport5007.fetch(
          `business-policy/v1/business-policy-sets?sourceMarketplaceId=${convertSourceMarketsEnumsToStrings(
            currentDiscoverMarket,
          )}&destinationMarketplace=${convertDestinationMarketsEnumsToStrings(self.listingMarket)}`,
          null,
          {
            'Seller-Account-Id': currentSellerAccountCode,
          },
        );
        self.sellerAccountPolicies = data;
        return data;
      } catch (error) {
        return null;
      }
    }),

    scheduleList: flow(function* scheduleList(scheduleTime, discoveryId) {
      try {
        const { data } = yield self.transport.post(
          `${self.urlRoot}/discovery-new/v1/item/schedule-listing`,
          {
            ScheduleListing: scheduleTime.format(),
            DiscoveryId: discoveryId,
          },
        );
        console.log(data);
        return true;
      } catch (error) {
        return false;
      }
    }),

    addLabel: flow(function* addLabels(reqData) {
      try {
        const currentDiscoveryId = reqData.discoveryId;
        const { data } = yield self.transport.post(`${self.urlRoot}/discovery-new/v1/item/label`, {
          name: reqData.name,
          discoveryItemId: reqData.discoveryItemId,
        });

        const discovery = self.discoveries.get(currentDiscoveryId);

        const discoveryItem = discovery.discoveryItems.find(x => x.id === data.discoveryItemId);
        discoveryItem.itemLabels.push(data);
        return data;
      } catch (error) {
        return error;
      }
    }),

    removeLabel: flow(function* removeLabel(labelId, discoveryId, discoveryItemId) {
      try {
        const { data } = yield self.transport.post(
          `${self.urlRoot}/discovery-new/v1/item/remove-label`,
          { discoveryItemLabelId: labelId },
        );
        if (data.isSuccessful) {
          const discovery = self.discoveries.get(discoveryId);
          const discoveryItem = discovery.discoveryItems.find(x => x.id === discoveryItemId);
          discoveryItem.itemLabels = discoveryItem.itemLabels.filter(label => label.id !== labelId);
        }
        return data;
      } catch (error) {
        return error;
      }
    }),

    updateSpecific: flow(function* updateSpecific(discoveryId, itemId, specificId, newValue) {
      const body = {
        id: specificId,
        propsToUpdate: newValue,
      };

      try {
        const { data, config } = yield self.transport.put(
          `${self.urlRoot}/discovery-new/v1/item/${itemId}/update-specification`,
          body,
          null,
          {},
        );
        if (data.isSuccess) {
          const parsedConfig = JSON.parse(config.data);
          const veroFailedWords = checkForVeroFailedWordsToUpdate(data);
          self.updateDiscoveryItemsInternal(
            discoveryId,
            [{ id: itemId, isVariant: false }],
            veroFailedWords,
          );
          return parsedConfig;
        }
        return null;
      } catch (error) {
        return error;
      }
    }),

    getOptimizedValues: flow(function* getOptimizedValues(discoveryId, itemId) {
      const currentDiscovery = self.discoveries.get(discoveryId);
      const item = currentDiscovery.discoveryItems.find(di => di.id === itemId);
      const itemToBeOptimized = JSON.parse(JSON.stringify(item));
      currentDiscovery.setDiscoveryItemsBeforeOptimization([itemToBeOptimized]);
      const params = {
        discoveryItemId: itemId,
        rotate: self.rotateImageOptimization,
        collage: self.collageImageOptimization,
      };

      const stringParams = qs.stringify(params, { arrayFormat: 'repeat', encode: false });
      const { data } = yield self.transport.fetch(
        `${self.urlRoot}/discovery-new/v1/item/optimized-values/?${stringParams}`,
        null,
        {},
      );

      return data;
    }),

    saveOptimization: flow(function* saveOptimization(itemId) {
      const body = {
        discoveryItemId: itemId,
        rotate: self.rotateImageOptimization,
        collage: self.collageImageOptimization,
      };
      const { data } = yield self.transport.post(
        `${self.urlRoot}/discovery-new/v1/item/optimize`,
        body,
        {},
      );

      self.rotateImageOptimization = false;
      self.collageImageOptimization = false;

      return data;
    }),

    getBulkOptimizedValues: flow(function* getBulkOptimizedValues(discoveryId) {
      const currentDiscovery = self.discoveries.get(discoveryId);
      const discoveryItemIdsList = currentDiscovery.rowsSelected;
      const itemsToBeOptimized = [];

      discoveryItemIdsList.forEach(itemId => {
        const itemToBeOptimized = {
          ...currentDiscovery.discoveryItems.find(di => di.id === itemId),
        };
        itemsToBeOptimized.push(itemToBeOptimized);
      });
      currentDiscovery.setDiscoveryItemsBeforeOptimization(itemsToBeOptimized);

      const params = {
        discoveryItemIds: discoveryItemIdsList,
        rotate: self.rotateImageOptimization,
        collage: self.collageImageOptimization,
      };

      const stringParams = qs.stringify(params, { arrayFormat: 'repeat', encode: false });
      const { data } = yield self.transport.fetch(
        `${self.urlRoot}/discovery-new/v1/item/bulk-optimized-values/?${stringParams}`,
        {},
        null,
      );
      self.rotateImageOptimization = false;
      self.collageImageOptimization = false;

      return data;
    }),

    revertBulkToOriginal: discoveryId => {
      const currentDiscovery = self.discoveries.get(discoveryId);
      const discoveryItemIdsList = currentDiscovery.rowsSelected;
      let originalDiscoveryItems = currentDiscovery.discoveryItemsBeforeOptimization;

      discoveryItemIdsList.forEach(itemId => {
        const itemToRevert = currentDiscovery.discoveryItems.find(di => di.id === itemId);
        const originalDiscoveryItem = originalDiscoveryItems.find(or => or.id === itemId);

        if (itemToRevert && originalDiscoveryItem) {
          self.updateDiscoveryItemsInternal(discoveryId, [itemToRevert], {
            title: originalDiscoveryItem.title,
            description: originalDiscoveryItem.description,
            itemSpecifics: originalDiscoveryItem.itemSpecifics,
            mainImageUrl: originalDiscoveryItem.mainImageUrl,
            imageS: originalDiscoveryItem.mainImageUrl,
            areOptimizedValuesShown: false,
          });
          originalDiscoveryItems = originalDiscoveryItems.filter(item => {
            return item.id !== itemToRevert.id;
          });
        }
      });

      self.rotateImageOptimization = false;
      self.collageImageOptimization = false;

      currentDiscovery.setDiscoveryItemsBeforeOptimization(originalDiscoveryItems);
    },

    saveBulkOptimization: flow(function* saveBulkOptimization(discoveryId) {
      const currentDiscovery = self.discoveries.get(discoveryId);
      const discoveryItemIdsList = currentDiscovery.rowsSelected;
      const body = {
        discoveryItemIds: discoveryItemIdsList,
        rotate: self.rotateImageOptimization,
        collage: self.collageImageOptimization,
      };
      const { data } = yield self.transport.post(
        `${self.urlRoot}/discovery-new/v1/item/bulk-optimize`,
        body,
        {},
      );

      self.rotateImageOptimization = false;
      self.collageImageOptimization = false;

      return data;
    }),

    setAsOptimizedBulkItems: discoveryId => {
      const currentDiscovery = self.discoveries.get(discoveryId);
      const discoveryItemIdsList = currentDiscovery.rowsSelected;

      discoveryItemIdsList.forEach(itemId => {
        const itemOptimized = currentDiscovery.discoveryItems.find(di => di.id === itemId);
        self.updateDiscoveryItemsInternal(discoveryId, [itemOptimized], {
          isOptimized: true,
          areOptimizedValuesShown: false,
        });
      });
    },

    setDiscoverMarket: val => {
      self.discoverMarket = val;
    },

    setDiscoverMethod: val => {
      self.discoverMethod = val;
    },

    setDiscoverValue: val => {
      self.discoverValue = val;
    },

    setDiscoverValueString: val => {
      self.discoverValueString = val;
    },

    setListingSelected: val => {
      self.listingMarket = val;
    },

    setStrategy: val => {
      self.discoverStrategy = val;
    },

    getSellersAccounts: flow(function* getSellersAccounts() {
      try {
        const { data } = yield self.transport5007.fetch(
          `seller-account/v1/seller-accounts?marketplaceId=${convertDestinationMarketsEnumsToStrings(
            self.listingMarket,
          )}`,
          null,
          {},
        );
        self.sellersAccounts = data;
        return data;
      } catch (error) {
        return error;
      }
    }),

    findItemId: flow(function* findItemId(discoveryId) {
      try {
        const { data, status } = yield self.transport5007.fetch(
          `seller-listing/v1/Items/discovery/${discoveryId}`,
          null,
          {},
        );
        if (status === 204 || !data) {
          return null;
        }
        return data;
      } catch (error) {
        console.error('', error);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'Error fetching item id from discovery id',
        });
        console.error(error);
        return null;
      }
    }),

    setSellerAccount: account => {
      self.sellerAccountCode = account.id;
    },

    /* eslint-disable */
    rtUpdate: message => {
      if (message.Type == WSMessage.MessageTypes.Custom) {
        if (message.Action == 'discovery') {
          let value = JSON.parse(message.Value);

          let o = self.qList.spliceList('completedDiscoveries', value.Id);
          if (o == null) o = self.qList.spliceList('discoveriesInProgress', value.Id);
          if (o == null) o = self.qList.spliceList('failedDiscoveries', value.Id);
          if (o == null) o = self.qList.spliceList('listedDiscoveries', value.Id);
          if (o != null) {
            self.qList.addToList(value.List, o, value.StatusId, value.ItemsDiscovered);
          }
        }
        if (message.Action == 'discoveryItem') {
          let value = JSON.parse(message.Value);
          self.qList.updateDiscoveryRecord(value.Id, value.ItemsDiscovered);
        }
        self.getQList();
      }
    },
    /* eslint-enable */

    initStore: flow(function* initStore() {
      websockets.addListener(WSMessage.Stores.Discovery, self.rtUpdate);
      self.setDiscoverMarket(0);
      self.setDiscoverMethod(0);
      self.setDiscoverValue([]);
      self.setListingSelected(0);
      self.setStrategy(0);
      self.setIsWithVariations(false);
      self.setAutomaticallyListItems(false);
      self.setSkipVeroValidation(false);
      self.setSkipOptimization(false);
      self.setRotateImage(false);
      self.setUseCollageImage(false);
      yield self.getSellersAccounts();
    }),
  }));

export default DiscoveryStore;
